import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  CreateGroupRequestModel,
  GroupModel,
  Groups,
  GroupVersions,
} from "../../../type/response/GroupManagementInitResponse";

type EditGroupType = {
  parent: GroupModel | null;
  group: Groups;
  groupVersion: GroupVersions | null;
  oldGroup: GroupModel | null;
};

/**
 * フォーム初期値
 */
const formDefaultValues: EditGroupType = {
  parent: null,
  group: { id: null, name: "", isDeleted: false },
  groupVersion: null,
  oldGroup: null,
};

/**
 * グループ新規作成ダイアログコンポーネント
 * - バージョン・親グループ・グループ名・旧グループを入力して新規グループを作成する
 * - props: open (ダイアログ表示状態), onClose (閉じるコールバック),
 *          groupModelList (グループ一覧), groupVersionList (バージョン一覧),
 *          displayVersion (現在表示中のバージョン), createGroup (作成コールバック),
 *          parent (親グループ)
 */
export default function EditGroupDialog({
  open,
  onClose,
  groupModelList,
  groupVersionList,
  displayVersion,
  createGroup,
  parent,
}: {
  open: boolean;
  onClose: () => void;
  groupModelList: GroupModel[];
  groupVersionList: GroupVersions[];
  displayVersion: GroupVersions | null;
  createGroup: (model: CreateGroupRequestModel, idempotencyKey: string) => Promise<string | null>;
  parent: GroupModel | null;
}) {
  const form = useForm<EditGroupType>({
    mode: "onChange",
    defaultValues: formDefaultValues,
  });
  const {
    control,
    formState: { errors },
    reset,
    getValues,
    handleSubmit,
    resetField,
  } = form;

  /**
   * 初期表示
   */
  useEffect(() => {
    reset({
      ...formDefaultValues,
      parent: parent,
      groupVersion: displayVersion,
    });
  }, [displayVersion, parent, reset]);

  /**
   * 画面を閉じたらフォームをリセットする
   */
  const handleClose = () => {
    reset({
      ...formDefaultValues,
      parent: parent,
      groupVersion: displayVersion,
    });
    onClose();
  };

  /**
   * 画面で開いているバージョンを保持する
   */
  const selectedVersion = useWatch({ control, name: "groupVersion" });

  /**
   * 新規作成ダイアログにて、バージョンの変更を検知し、値を更新する
   */
  const [currentVersion, setCurrentVersion] = useState(displayVersion?.version ?? "");
  const prevVersionRef = useRef(currentVersion);

  /** 二重送信防止フラグ */
  const isSubmitting = useRef<boolean>(false);

  /** 冪等性キー：ダイアログを開くたびに新しいUUIDを生成し、閉じると破棄する */
  const idempotencyKeyRef = useRef<string>("");
  useEffect(() => {
    if (open) {
      idempotencyKeyRef.current = crypto.randomUUID();
    }
  }, [open]);

  useEffect(() => {
    const newVersion = selectedVersion?.version ?? "";

    // 本当にバージョンが変わった場合だけリセット
    if (prevVersionRef.current !== newVersion) {
      setCurrentVersion(newVersion);
      resetField("parent");
      prevVersionRef.current = newVersion;
    }
  }, [selectedVersion, resetField]);


  /**
   * バージョンの値が更新されるごとに親・子グループのAutocompleteのリストを更新する
   */
  const filteredGroupModelList = React.useMemo(() => {
    return groupModelList
      .filter(item => item.groupVersion?.version === currentVersion)
      .sort((a, b) =>
        (a.groupVersion?.version ?? "") > (b.groupVersion?.version ?? "") ? 1 : -1
      );
  }, [groupModelList, currentVersion]);
  

  /**
   * グループ新規作成
   */
  const handleCreate = async () => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    try {
      const model: CreateGroupRequestModel = {
        group: getValues("group"),
        groupVersion: getValues("groupVersion"),
        parentGroupId: getValues("parent")?.group.id ?? null,
        oldGroup: getValues("oldGroup.group"),
      };
      const result = await createGroup(model, idempotencyKeyRef.current);
      if (result) handleClose();
    } finally {
      isSubmitting.current = false;
    }
  };

  return (
    <Dialog open={open} onClose={() => handleClose()} maxWidth="sm" fullWidth>
      <DialogTitle>グループ新規作成</DialogTitle>
      <DialogContent>
        <Grid sx={{ mt: 2 }}>
              <FormControl sx={{ width: 200 }} required>
                <InputLabel id="groupVersionLabel">バージョン</InputLabel>
                <Controller
                  control={control}
                  name="groupVersion"
                  rules={{ required: "この項目は入力必須です。" }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="groupVersionLabel"
                      label="バージョン"
                      value={
                        field.value?.version ?? displayVersion?.version ?? ""
                      }
                      onChange={(e) => {
                        const targetList = groupVersionList.filter(
                          (v) => v.version === e.target.value
                        );
                        field.onChange(
                          targetList.length > 0 ? targetList[0] : null
                        );
                      }}
                    >
                      {groupVersionList.map((v) => (
                        <MenuItem key={v.id} value={v.version}>
                          {v.version}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
        </Grid>
        <Box sx={{ my: 1, width: 400 }}>
          <Controller
            control={control}
            name="parent"
            render={({ field }) => (
              <Autocomplete
                options={filteredGroupModelList}
                groupBy={(option) => option.groupVersion?.version ?? ""}
                getOptionLabel={(option) => option.group.name}
                value={field.value}
                onChange={(_, value) => field.onChange(value)}
                renderInput={(params) => (
                  <TextField {...params} label="親グループ" fullWidth />
                )}
              />
            )}
          />
        </Box>
        <Grid container sx={{ gap: 2, alignItems: "center", my: 2 }}>
          <Grid>
            <Controller
              control={control}
              name="group.name"
              rules={{
                required: "この項目は入力必須です。",
                validate: (data) => {
                  if (
                    groupModelList.some(
                      (v) =>
                        v.groupVersion?.id === getValues("groupVersion")?.id &&
                        v.group.name === data
                    )
                  ) {
                    return "このグループ名は登録されています。";
                  }
                },
              }}
              render={({ field }) => (
                <TextField
                  required
                  {...field}
                  error={!!errors.group?.name}
                  helperText={errors.group?.name?.message}
                  label="グループ名"
                  sx={{ width: 400 }}
                />
              )}
            />
          </Grid>
          <Grid>
            <Controller
              control={control}
              name="oldGroup"
              render={({ field }) => (
                <Autocomplete
                  options={groupModelList.sort((a, b) =>
                    (a.groupVersion?.version ?? "") >
                    (b.groupVersion?.version ?? "")
                      ? 1
                      : -1
                  )}
                  groupBy={(option) => option.groupVersion?.version ?? ""}
                  getOptionLabel={(option) => option.group.name}
                  value={field.value}
                  onChange={(_, value) => field.onChange(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="旧グループ"
                      sx={{ width: 400 }}
                    />
                  )}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Button variant="outlined" onClick={() => handleClose()}>
            キャンセル
          </Button>
          <Button variant="contained" onClick={handleSubmit(handleCreate)}>
            登録
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
